// localStorage utilities for MCG Classic Mini e-commerce
// Handles cart, customer details, and order information persistence

export interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  deliveryInstructions: string;
  specialInstructions: string;
  deliveryMethod: 'delivery' | 'collection';
}

export interface OrderData {
  customer: CustomerDetails;
  cartItems: any[];
  shippingInfo: any;
  totalAmount: number;
  reference: string;
  timestamp: number;
}

// Storage keys
const STORAGE_KEYS = {
  CART: 'mcg_cart_items',
  CUSTOMER: 'mcg_customer_details',
  DRAFT_ORDER: 'mcg_draft_order',
  ORDER_HISTORY: 'mcg_order_history'
} as const;

// Generic storage utilities
const storage = {
  save: <T>(key: string, data: T): boolean => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      console.warn(`Failed to save ${key} to localStorage:`, error);
      return false;
    }
  },

  load: <T>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.warn(`Failed to load ${key} from localStorage:`, error);
      return null;
    }
  },

  remove: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`Failed to remove ${key} from localStorage:`, error);
      return false;
    }
  },

  clear: (): boolean => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.warn('Failed to clear MCG localStorage:', error);
      return false;
    }
  }
};

// Customer details utilities
export const customerStorage = {
  save: (details: Partial<CustomerDetails>): boolean => {
    const existing = customerStorage.load() || {};
    const updated = { ...existing, ...details };
    return storage.save(STORAGE_KEYS.CUSTOMER, updated);
  },

  load: (): CustomerDetails | null => {
    return storage.load<CustomerDetails>(STORAGE_KEYS.CUSTOMER);
  },

  clear: (): boolean => {
    return storage.remove(STORAGE_KEYS.CUSTOMER);
  },

  // Auto-fill form data
  getFormDefaults: (): Partial<CustomerDetails> => {
    const saved = customerStorage.load();
    if (!saved) return {};
    
    return {
      name: saved.name || '',
      email: saved.email || '',
      phone: saved.phone || '',
      address: saved.address || '',
      city: saved.city || '',
      postalCode: saved.postalCode || '',
      deliveryInstructions: saved.deliveryInstructions || '',
      deliveryMethod: saved.deliveryMethod || 'delivery',
      specialInstructions: ''  // Always start fresh for special instructions
    };
  }
};

// Draft order utilities (for checkout persistence)
export const draftOrderStorage = {
  save: (orderData: Partial<OrderData>): boolean => {
    const draft = {
      ...orderData,
      timestamp: Date.now()
    };
    return storage.save(STORAGE_KEYS.DRAFT_ORDER, draft);
  },

  load: (): Partial<OrderData> | null => {
    const draft = storage.load<Partial<OrderData> & { timestamp: number }>(STORAGE_KEYS.DRAFT_ORDER);
    
    // Check if draft is too old (24 hours)
    if (draft && draft.timestamp) {
      const hoursSince = (Date.now() - draft.timestamp) / (1000 * 60 * 60);
      if (hoursSince > 24) {
        draftOrderStorage.clear();
        return null;
      }
    }
    
    return draft;
  },

  clear: (): boolean => {
    return storage.remove(STORAGE_KEYS.DRAFT_ORDER);
  }
};

// Order history utilities
export const orderHistoryStorage = {
  add: (order: { id: string; reference: string; email: string; timestamp: number }): boolean => {
    const history = orderHistoryStorage.load();
    const updated = [...history, order];
    
    // Keep only last 10 orders
    const limited = updated.slice(-10);
    
    return storage.save(STORAGE_KEYS.ORDER_HISTORY, limited);
  },

  load: (): Array<{ id: string; reference: string; email: string; timestamp: number }> => {
    return storage.load(STORAGE_KEYS.ORDER_HISTORY) || [];
  },

  clear: (): boolean => {
    return storage.remove(STORAGE_KEYS.ORDER_HISTORY);
  },

  // Get recent orders for current email
  getForEmail: (email: string): Array<{ id: string; reference: string; timestamp: number }> => {
    const history = orderHistoryStorage.load();
    return history
      .filter(order => order.email.toLowerCase() === email.toLowerCase())
      .sort((a, b) => b.timestamp - a.timestamp);
  }
};

// Utility to check localStorage availability
export const isStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, 'test');
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

// Hook for storage state
import { useState, useEffect } from 'react';

export const useLocalStorage = <T>(key: string, defaultValue: T) => {
  const [value, setValue] = useState<T>(() => {
    if (!isStorageAvailable()) return defaultValue;
    const saved = storage.load<T>(key);
    return saved !== null ? saved : defaultValue;
  });

  useEffect(() => {
    if (isStorageAvailable()) {
      storage.save(key, value);
    }
  }, [key, value]);

  return [value, setValue] as const;
};

export default storage;
